import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Divider from '@mui/material/Divider';
import MenuIcon from '@mui/icons-material/Menu';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import InsightsIcon from '@mui/icons-material/Insights';
import SettingsIcon from '@mui/icons-material/Settings';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LoginIcon from '@mui/icons-material/Login';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import FavoriteIcon from '@mui/icons-material/Favorite';
import LogoutIcon from '@mui/icons-material/Logout';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import GavelIcon from '@mui/icons-material/Gavel';
import LanguageIcon from '@mui/icons-material/Language';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useShallow } from "zustand/react/shallow";

import { ROUTES } from "~/const";
import { trans } from "~/utils/trans";
import { userStore } from "~/stores/user";
import { setJWT } from "~/utils/utils";
import { getHamburgerLinks, getInfoLinks, ILinkItem, getAdminLinks, getStatisticsLinks, getProfileLinks } from "~/providers/links";
import { myUseState } from "~/hooks/myUseState";
import DonationModal from "~/modals/DonationModal";

import "./style.less";

interface IState {
	anchorEl: HTMLElement;
	donation: boolean;
}

// ikony polozek menu podle cesty
const ICONS: Record<string, ReactNode> = {
	[ROUTES.schedule]: <RestaurantMenuIcon fontSize="small" />,
	[ROUTES.weight]: <InsightsIcon fontSize="small" />,
	[ROUTES.profile]: <SettingsIcon fontSize="small" />,
	[ROUTES.recipes]: <AdminPanelSettingsIcon fontSize="small" />,
	[ROUTES.login]: <LoginIcon fontSize="small" />,
	[ROUTES.register]: <PersonAddIcon fontSize="small" />,
	[ROUTES.verify]: <MarkEmailReadIcon fontSize="small" />,
	[ROUTES.howitworks]: <HelpOutlineIcon fontSize="small" />,
	[ROUTES.conditions]: <GavelIcon fontSize="small" />,
	[ROUTES.home]: <LanguageIcon fontSize="small" />,
};

function isExternal(route: string) {
	return route.startsWith("http");
}

export default function HamburgerMenu() {
	const navigate = useNavigate();
	const { userData, logoutStore } = userStore(useShallow(userState => ({
		userData: userState.user,
		logoutStore: userState.logout,
	})));
	const { state, updateState } = myUseState<IState>({
		anchorEl: null,
		donation: false,
	});
	const open = Boolean(state.anchorEl);

	function hambugerClick(event) {
		updateState({
			anchorEl: event.currentTarget,
		});
	}

	function handleClose() {
		updateState({
			anchorEl: null,
		});
	}

	function linkClick(route: string) {
		updateState({
			anchorEl: null,
		});

		// odkazy na web doitforyourself.cz do nove zalozky, aplikace zustane otevrena
		if (isExternal(route)) {
			window.open(route, "_blank", "noopener,noreferrer");
		} else {
			navigate(route);
		}
	}

	function getSelected(item: ILinkItem): boolean {
		let links = [];

		switch (item.type) {
			case "admin":
				links = getAdminLinks();
				break;

			case "statistics":
				links = getStatisticsLinks();
				break;

			case "profile":
				links = getProfileLinks();
				break;

			default:
		}

		if (links.length > 0) {
			const filtered = links.filter(linkItem => location.pathname.indexOf(linkItem.route) !== -1);

			return filtered.length !== 0;
		}

		return location.pathname === item.route || (item.route === ROUTES.login && location.pathname === "/");
	}

	function logout() {
		setJWT("");
		logoutStore();
		updateState({
			anchorEl: null,
		});
		navigate(ROUTES.login);
	}

	return <>
		<Button sx={{ padding: "0" }} onClick={hambugerClick} aria-label="Menu" aria-haspopup="true" aria-expanded={open}>
			<MenuIcon className="MyMenu" />
		</Button>
		<Menu
			anchorEl={state.anchorEl}
			open={open}
			onClose={handleClose}
			anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
			transformOrigin={{ vertical: "top", horizontal: "right" }}
			slotProps={{ paper: { className: "hamburgerMenu" } }}
		>
			{ getHamburgerLinks(userData.email !== "", userData.permissions === 1).map(item => <MenuItem
				onClick={() => linkClick(item.route)}
				key={item.route}
				selected={getSelected(item)}
				className="hamburgerMenu__item"
			>
				<ListItemIcon>{ICONS[item.route]}</ListItemIcon>
				<ListItemText>{item.title}</ListItemText>
			</MenuItem>) }
			<MenuItem onClick={() => updateState({ anchorEl: null, donation: true })} className="hamburgerMenu__item hamburgerMenu__item--donation">
				<ListItemIcon><FavoriteIcon fontSize="small" /></ListItemIcon>
				<ListItemText>{trans("menu.donation")}</ListItemText>
			</MenuItem>
			{ userData.email && <MenuItem onClick={logout} className="hamburgerMenu__item">
				<ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
				<ListItemText>{trans("menu.logout")}</ListItemText>
			</MenuItem> }
			<Divider className="hamburgerMenu__divider" />
			<ListSubheader className="hamburgerMenu__subheader">{trans("menu.info")}</ListSubheader>
			{ getInfoLinks().map(item => <MenuItem onClick={() => linkClick(item.route)} key={item.route} className="hamburgerMenu__item hamburgerMenu__item--info">
				<ListItemIcon>{ICONS[item.route]}</ListItemIcon>
				<ListItemText>{item.title}</ListItemText>
				<OpenInNewIcon className="hamburgerMenu__external" />
			</MenuItem>) }
		</Menu>
		{ state.donation && <DonationModal onClose={() => updateState({ donation: false })} /> }
	</>;
}
